/**
 * Every permission the application knows, and the one place it is declared.
 *
 * The server enforces these (`@Requires(...)` on a route, `hasPermission` in a
 * service), the client hides what the server would refuse, and the roles
 * screen draws its matrix from `PERMISSION_MODULES`. A role in the database is
 * a set of these keys; a key the catalog no longer lists is ignored when a
 * user's permissions are resolved, so deleting an entry here is safe.
 *
 * ## Adding a permission
 *
 * Add it to the module it belongs to, give it `requires` if it is meaningless
 * without another (editing without viewing), and gate the route with it. A
 * new permission is granted to nobody but the super role until an admin ticks
 * it — that is the safe default for something nobody has been told about.
 */

import { CLINICAL_MODULES } from './permission-catalog-clinical';
import { OPERATIONS_MODULES } from './permission-catalog-operations';
import type { PermissionDef, PermissionModule } from './permission-defs';

export type { PermissionDef, PermissionModule } from './permission-defs';
export { SYSTEM_ROLES, type SystemRoleKey } from './system-roles';

export const PERMISSION_MODULES = [
  ...CLINICAL_MODULES,
  ...OPERATIONS_MODULES,
] as const satisfies readonly PermissionModule[];

type ItemPermissionKeys<I> = I extends { permissions: infer P } ? keyof P & string : never;
type ModulePermissionKeys<M> = M extends { items: readonly (infer I)[] }
  ? ItemPermissionKeys<I>
  : never;

export type Permission = ModulePermissionKeys<(typeof PERMISSION_MODULES)[number]>;

/** Every permission, flat, keyed for lookup. */
export const PERMISSIONS: Readonly<Record<Permission, PermissionDef & { module: string }>> =
  Object.fromEntries(
    PERMISSION_MODULES.flatMap((module) =>
      module.items.flatMap((item) =>
        Object.entries(item.permissions as Readonly<Record<string, PermissionDef>>).map(
          ([key, def]) => [key, { ...def, module: module.key }],
        ),
      ),
    ),
  ) as Readonly<Record<Permission, PermissionDef & { module: string }>>;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as readonly Permission[];

export function isPermission(key: string): key is Permission {
  return Object.hasOwn(PERMISSIONS, key);
}

/**
 * `keys` plus everything they require, transitively. Unknown keys are dropped.
 *
 * Applied when a role is saved, so the stored set is already closed, and again
 * when a user's permissions are resolved, so an override cannot grant "edit"
 * without the "view" it depends on.
 */
export function withRequirements(keys: Iterable<string>): Set<Permission> {
  const out = new Set<Permission>();
  const pending = [...keys];
  while (pending.length > 0) {
    const key = pending.pop()!;
    if (!isPermission(key) || out.has(key)) continue;
    out.add(key);
    pending.push(...(PERMISSIONS[key].requires ?? []));
  }
  return out;
}

/** Every permission that (transitively) requires `key` — what losing it takes with it. */
export function dependentsOf(key: Permission): Set<Permission> {
  const out = new Set<Permission>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const candidate of ALL_PERMISSIONS) {
      if (out.has(candidate)) continue;
      const requires = PERMISSIONS[candidate].requires ?? [];
      if (
        requires.some((requirement) => requirement === key || out.has(requirement as Permission))
      ) {
        out.add(candidate);
        grew = true;
      }
    }
  }
  return out;
}

export type PermissionEffect = 'allow' | 'deny';

interface PermissionOverride {
  permission: string;
  effect: PermissionEffect;
}

/**
 * What one user may do: their role's permissions, plus what they are allowed,
 * minus what they are denied.
 *
 * A super role holds everything, including permissions added after it was
 * created, and overrides do not apply to it — the account that can repair the
 * roles must not be able to lock itself out of them one tick at a time.
 *
 * A deny also removes whatever depends on the denied key, so denying
 * `patient.view` cannot leave a working `patient.edit` behind it.
 */
export function resolveEffective({
  rolePermissions,
  overrides,
  isSuper,
}: {
  rolePermissions: Iterable<string>;
  overrides: readonly PermissionOverride[];
  isSuper: boolean;
}): Set<Permission> {
  if (isSuper) return new Set(ALL_PERMISSIONS);

  const granted = withRequirements([
    ...rolePermissions,
    ...overrides
      .filter((override) => override.effect === 'allow')
      .map((override) => override.permission),
  ]);
  for (const { permission, effect } of overrides) {
    if (effect !== 'deny' || !isPermission(permission)) continue;
    granted.delete(permission);
    for (const dependent of dependentsOf(permission)) granted.delete(dependent);
  }
  return granted;
}
